import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { 
  MessageSquare, 
  ArrowLeft,
  Loader2,
  Save,
  FileSignature,
  Building,
  User,
  Phone,
  Mail,
  Percent,
  FileText,
  Check
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RetainerSettingsPage = () => {
  const {  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState({
    firm_name: '',
    firm_address: '',
    firm_phone: '',
    firm_email: '',
    attorney_name: '',
    attorney_bar_number: '',
    contingency_fee: '33.33%',
    include_case_summary: true
  });

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/retainer/template`);
      setTemplate(response.data);
    } catch (error) {
      console.error('Failed to fetch template:', error);
      toast.error('Failed to load retainer settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/retainer/template`, template);
      toast.success('Retainer settings saved!');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save retainer settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setTemplate(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  IntakePal
                </span>
              </div>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="save-retainer-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <FileSignature className="inline w-8 h-8 mr-2 text-purple-600" />
            Retainer Agreement Settings
          </h1>
          <p className="text-slate-600">
            Customize the information that appears on your generated retainer agreements.
          </p>
        </div>

        <div className="grid gap-6">
          {/* Firm Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-teal-500" />
                Firm Information
              </CardTitle>
              <CardDescription>
                Your law firm&apos;s contact details for the agreement header
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firm_name">Firm Name</Label>
                  <Input
                    id="firm_name"
                    value={template.firm_name}
                    onChange={(e) => handleChange('firm_name', e.target.value)}
                    placeholder="Smith & Associates Law Firm"
                    data-testid="firm-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="firm_phone">Firm Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="firm_phone"
                      value={template.firm_phone}
                      onChange={(e) => handleChange('firm_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="firm_address">Firm Address</Label>
                <Textarea
                  id="firm_address"
                  value={template.firm_address}
                  onChange={(e) => handleChange('firm_address', e.target.value)}
                  placeholder="123 Main Street, Suite 100&#10;Atlanta, GA 30301"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="firm_email">Firm Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="firm_email"
                    type="email"
                    value={template.firm_email}
                    onChange={(e) => handleChange('firm_email', e.target.value)}
                    placeholder="contact@lawfirm.com"
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attorney Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-teal-500" />
                Attorney Information
              </CardTitle>
              <CardDescription>
                The attorney who will sign the agreements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="attorney_name">Attorney Name</Label>
                  <Input
                    id="attorney_name"
                    value={template.attorney_name}
                    onChange={(e) => handleChange('attorney_name', e.target.value)}
                    placeholder="John Smith, Esq."
                    data-testid="attorney-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attorney_bar_number">Bar Number</Label>
                  <Input
                    id="attorney_bar_number"
                    value={template.attorney_bar_number}
                    onChange={(e) => handleChange('attorney_bar_number', e.target.value)}
                    placeholder="123456"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fee Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-teal-500" />
                Fee Structure
              </CardTitle>
              <CardDescription>
                Your contingency fee percentage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="contingency_fee">Contingency Fee</Label>
                <Input
                  id="contingency_fee"
                  value={template.contingency_fee}
                  onChange={(e) => handleChange('contingency_fee', e.target.value)}
                  placeholder="33.33%"
                  className="max-w-xs"
                  data-testid="contingency-fee-input"
                />
                <p className="text-xs text-slate-500">
                  Common rates: 33.33% (pre-litigation), 40% (after filing suit)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Document Options */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-500" />
                Document Options
              </CardTitle>
              <CardDescription>
                Customize what&apos;s included in the agreement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Include Case Summary</Label>
                  <p className="text-sm text-slate-500">
                    Add the automated case summary to the agreement
                  </p>
                </div>
                <Switch
                  checked={template.include_case_summary}
                  onCheckedChange={(checked) => handleChange('include_case_summary', checked)}
                  data-testid="include-summary-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Preview Info */}
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Ready to Generate</h3>
                  <p className="text-sm text-slate-600">
                    Once you save your settings, you can generate retainer agreements from any lead&apos;s detail page. 
                    Click &quot;Generate Retainer Agreement&quot; to create a PDF pre-filled with the client&apos;s information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default RetainerSettingsPage;
